import type { GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';
import { CasinoGamesQueryParams } from '../@types/casino';
import { useCasinoGames } from '../queries/casino';
import { useCasinoTableQuery } from './useCasinoTableQuery';

export const DEFAULT_CASINO_GAMES_QUERY_PARAMS: CasinoGamesQueryParams = {
  page: 1,
  limit: 25,
};

type CasinoGamesDefaultSort = Required<Pick<CasinoGamesQueryParams, 'sortColumn' | 'sortOrder'>>;

type UseCasinoGamesTableOptions = {
  initialQueryParams?: Partial<CasinoGamesQueryParams>;
  defaultSort?: CasinoGamesDefaultSort;
};

export const useCasinoGamesTable = ({ initialQueryParams, defaultSort }: UseCasinoGamesTableOptions = {}) => {
  const { queryParams, setQueryParams, paginationModel, updateQueryParams, changeQuery, handleSearch } =
    useCasinoTableQuery<CasinoGamesQueryParams>({
      defaultQuery: { ...DEFAULT_CASINO_GAMES_QUERY_PARAMS, ...defaultSort },
      initialQueryParams,
      usePersistedFilters: true,
      searchField: 'search',
      emptyFields: [
        'search',
        'providerName',
        'aggregatorName',
        'categoryId',
        'categoryFilter',
        'gameType',
        'sortGames',
        'sortColumn',
        'sortOrder',
      ],
    });
  const queryResult = useCasinoGames(queryParams);
  const handleSort = useCallback(
    (model: GridSortModel) => {
      const sortModelItem = model[0];

      changeQuery({
        sortColumn: sortModelItem?.field ?? defaultSort?.sortColumn,
        sortOrder: sortModelItem?.sort ?? defaultSort?.sortOrder,
      });
    },
    [changeQuery, defaultSort?.sortColumn, defaultSort?.sortOrder]
  );

  return {
    ...queryResult,
    queryParams,
    setQueryParams,
    paginationModel,
    updateQueryParams,
    changeQuery,
    handleSearch,
    handleSort,
  };
};
